import { PRODUCT_NAME } from "@mailhand/core/brand";
import type { Inbox, SendingDomain } from "@mailhand/core/schemas";
import { emailSchema } from "@mailhand/core/schemas";
import { type SubmitEvent, useState } from "react";
import { useApi } from "../../app/services.js";
import { useTenant } from "../../app/tenant.js";
import { useTask } from "../../app/useTask.js";
import { Card, CardHeader, CopyButton, Button, Field, Input, Select, Switch } from "../../ui/index.js";
import { InboxCreateForm } from "../shared/InboxCreateForm.js";

const SHARED = "shared";

function splitSendFrom(inbox: Inbox): { domain: string; local: string } {
  if (inbox.sendFrom === SHARED) return { domain: SHARED, local: "support" };
  const [local = "", domain = ""] = inbox.sendFrom.split("@");
  return { domain, local };
}

export function InboxesSection() {
  const { view } = useTenant();
  const [adding, setAdding] = useState(false);
  return (
    <Card id="inboxes">
      <CardHeader
        eyebrow="Inboxes"
        title="Forward addresses"
        aside={
          adding ? undefined : (
            <Button size="sm" variant="ghost" onClick={() => { setAdding(true); }}>
              Add inbox
            </Button>
          )
        }
      />
      <div className="stack-lg">
        {view.inboxes.map((inbox) => (
          <InboxEditor key={inbox.address} inbox={inbox} readyDomains={view.domains.filter((domain) => domain.state === "ready")} />
        ))}
        {adding || view.inboxes.length === 0 ? <InboxCreateForm onCreated={() => { setAdding(false); }} /> : null}
      </div>
    </Card>
  );
}

function InboxEditor({ inbox, readyDomains }: { inbox: Inbox; readyDomains: SendingDomain[] }) {
  const api = useApi();
  const { view, setView } = useTenant();
  const task = useTask();
  const initial = splitSendFrom(inbox);
  const [displayName, setDisplayName] = useState(inbox.displayName);
  const [replyTo, setReplyTo] = useState(inbox.replyTo);
  const [sendDomain, setSendDomain] = useState(initial.domain);
  const [sendLocal, setSendLocal] = useState(initial.local);
  const [copyMe, setCopyMe] = useState(inbox.copyRepliesTo !== null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const reply = emailSchema.safeParse(replyTo.trim());
    const sendFrom = sendDomain === SHARED ? SHARED : emailSchema.safeParse(`${sendLocal.trim()}@${sendDomain}`);
    const next: Record<string, string> = {};
    if (displayName.trim() === "") next.displayName = "Type the name senders see.";
    if (!reply.success) next.replyTo = "Type an email address.";
    if (sendFrom !== SHARED && !sendFrom.success) next.sendFrom = "Type the part before the @.";
    setErrors(next);
    if (!reply.success || (sendFrom !== SHARED && !sendFrom.success) || Object.keys(next).length > 0) return;
    await task.run(async () => {
      const saved = await api.call("inboxPatch", {
        params: { address: inbox.address },
        body: {
          displayName: displayName.trim(),
          replyTo: reply.data,
          sendFrom: sendFrom === SHARED ? SHARED : sendFrom.data,
          copyRepliesTo: copyMe ? reply.data : null,
        },
      });
      setView({ ...view, inboxes: view.inboxes.map((existing) => (existing.address === saved.address ? saved : existing)) });
    }, "Inbox saved.");
  }

  return (
    <form className="subcard stack" onSubmit={(event) => void submit(event)} noValidate>
      <div className="address-line">
        <span className="mono address">{inbox.address}</span>
        <CopyButton value={inbox.address} />
      </div>
      <div className="form-grid">
        <Field label="Name on replies" error={errors.displayName}>
          {({ id, describedBy, invalid }) => <Input id={id} value={displayName} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { setDisplayName(event.target.value); }} />}
        </Field>
        <Field label="Reply-to (your support address)" error={errors.replyTo}>
          {({ id, describedBy, invalid }) => <Input id={id} type="email" value={replyTo} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { setReplyTo(event.target.value); }} />}
        </Field>
      </div>
      <div className="form-grid">
        <Field label="Send replies from" hint={readyDomains.length === 0 ? "Add a sending domain below to send from your own address." : undefined}>
          {({ id, describedBy }) => (
            <Select id={id} value={sendDomain} aria-describedby={describedBy} onChange={(event) => { setSendDomain(event.target.value); }}>
              <option value={SHARED}>The shared {PRODUCT_NAME} address</option>
              {readyDomains.map((domain) => (
                <option key={domain.domain} value={domain.domain}>
                  An address at {domain.domain}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {sendDomain === SHARED ? null : (
          <Field label="Address" error={errors.sendFrom}>
            {({ id, describedBy, invalid }) => (
              <div className="input-affix">
                <Input id={id} value={sendLocal} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { setSendLocal(event.target.value); }} />
                <span className="mono muted">@{sendDomain}</span>
              </div>
            )}
          </Field>
        )}
      </div>
      <Switch checked={copyMe} onChange={setCopyMe} label="Copy me on replies" description="Every reply is BCC'd to your reply-to address, so your mailbox shows what went out." />
      <div className="button-row">
        <Button type="submit" variant="primary" busy={task.busy}>
          Save inbox
        </Button>
      </div>
    </form>
  );
}
